/**
 * Utility functions for consistent date handling across the application
 */
import { getCurrentBusinessDate, type BusinessHours } from './businessDay'

export interface DateRange {
  from: Date
  to: Date
}

/**
 * Convert a date to YYYY-MM-DD format for HTML date inputs
 * This handles timezone issues properly
 */
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Create a proper DateRange object with time boundaries
 */
export const createDateRange = (from: Date, to: Date): DateRange => {
  const fromDate = new Date(from)
  fromDate.setHours(0, 0, 0, 0) // Start of day
  
  const toDate = new Date(to)
  toDate.setHours(23, 59, 59, 999) // End of day
  
  return { from: fromDate, to: toDate }
}

/**
 * Get default date range (today only).
 *
 * When businessHours is provided, "today" resolves to the restaurant's
 * current business day (which may still be yesterday's calendar date if the
 * business day crosses midnight and hasn't closed yet) instead of the
 * browser's calendar date.
 */
export const getDefaultDateRange = (businessHours?: BusinessHours): DateRange => {
  if (businessHours) {
    const businessDateStr = getCurrentBusinessDate(businessHours)
    const businessDate = parseDate(businessDateStr)
    return createDateRange(businessDate, businessDate)
  }

  const today = new Date()
  return createDateRange(today, today)
}

/**
 * Parse date from string and handle timezone properly
 * For date inputs (YYYY-MM-DD format)
 */
export const parseDate = (dateString: string): Date => {
  // For YYYY-MM-DD format, create date in local timezone
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-based
}

/**
 * Check if a date range is valid
 */
export const isValidDateRange = (dateRange: DateRange): boolean => {
  return dateRange.from <= dateRange.to
}

/**
 * Format date for API query parameters (YYYY-MM-DD format, timezone-safe)
 */
export const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format date range for API query parameters
 */
export const formatDateRangeForAPI = (dateRange: DateRange) => {
  return {
    from: dateRange.from.toISOString(),
    to: dateRange.to.toISOString()
  }
}